import { ExportResultCode } from '@opentelemetry/core';
import { SpanKind } from '@opentelemetry/api';
const nano = (time) => (BigInt(time[0]) * 1000000000n + BigInt(time[1])).toString();
const text = (key, value) => ({ key, value: { stringValue: value } });
/** Serialize only operational fields. Never send SDK URL attributes or span events. */
export function serializeBrowserSpan(span) {
    if (span.kind !== SpanKind.CLIENT)
        return undefined;
    const attrs = span.attributes;
    let hostname = '';
    try {
        const url = new URL(String(attrs['http.url'] ?? attrs['url.full'] ?? ''), globalThis.location?.href);
        if (['http:', 'https:', 'ws:', 'wss:'].includes(url.protocol))
            hostname = url.hostname;
    }
    catch {
        /* Lifecycle records may have a provider instead of a URL. */
    }
    const status = Number(attrs['http.status_code'] ?? attrs['http.response.status_code']);
    const method = String(attrs['http.method'] ?? attrs['http.request.method'] ?? 'GET').toUpperCase();
    return {
        traceId: span.spanContext().traceId,
        spanId: span.spanContext().spanId,
        kind: 3,
        name: 'browser request',
        startTimeUnixNano: nano(span.startTime),
        endTimeUnixNano: nano(span.endTime),
        status: { code: span.status.code },
        attributes: [
            text('nava.browser.transport', String(attrs['nava.browser.transport'] ?? 'http')),
            text('server.address', hostname),
            text('http.request.method', ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS', 'CONNECT'].includes(method)
                ? method
                : 'GET'),
            ...(status >= 100 && status <= 599
                ? [{ key: 'http.response.status_code', value: { intValue: status } }]
                : []),
            ...['nava.browser.event', 'nava.browser.provider'].flatMap((key) => typeof attrs[key] === 'string' ? [text(key, attrs[key])] : []),
            ...(typeof attrs['nava.browser.duration_ms'] === 'number'
                ? [
                    {
                        key: 'nava.browser.duration_ms',
                        value: { doubleValue: attrs['nava.browser.duration_ms'] },
                    },
                ]
                : []),
        ],
    };
}
export class BrowserSpanExporter {
    endpoint;
    send;
    pending = new Set();
    constructor(endpoint, send) {
        this.endpoint = endpoint;
        this.send = send;
    }
    export(spans, callback) {
        const sanitized = spans.map(serializeBrowserSpan).filter((span) => span !== undefined);
        if (!sanitized.length) {
            callback({ code: ExportResultCode.SUCCESS });
            return;
        }
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 3000);
        this.pending.add(controller);
        // Fetch is captured before instrumentation. Uploads are measured at the BFF.
        void this.send(this.endpoint, {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ resourceSpans: [{ scopeSpans: [{ spans: sanitized }] }] }),
            signal: controller.signal,
            redirect: 'error',
            keepalive: true,
        })
            .then(async (response) => {
            await response.body?.cancel();
            callback({ code: response.ok ? ExportResultCode.SUCCESS : ExportResultCode.FAILED });
        })
            .catch(() => callback({ code: ExportResultCode.FAILED }))
            .finally(() => {
            clearTimeout(timer);
            this.pending.delete(controller);
        });
    }
    async shutdown() {
        for (const controller of this.pending)
            controller.abort();
        this.pending.clear();
    }
}
//# sourceMappingURL=BrowserSpanExporter.js.map